/** The runs, one row each; a click opens the run at `/experiments/:id`. */

import { useNavigate } from "react-router";
import { Box } from "@/components/ui/box";
import { DataTable, type Column } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/states";
import { formatPeriod } from "@/features/experiments/detail/trace-run";
import { isLive } from "@/features/experiments/status";
import { ExperimentStatusPill } from "@/features/experiments/status-pill";
import { formatDateTime, formatPercent, formatScore } from "@/lib/format";
import type { EvaluatorRun } from "./types";

/** "Golden v4" / "Prod traces (live) v12": the dataset it ran on. */
export function runDataset(r: EvaluatorRun): string {
  if (!r.dataset_name) return "—";
  const live = r.dataset_kind === "live" ? " (live)" : "";
  return `${r.dataset_name}${live}${r.dataset_version ? ` v${r.dataset_version}` : ""}`;
}

function Score({ run: r }: { run: EvaluatorRun }) {
  if (isLive(r.status) || (r.mean === null && r.status !== "completed"))
    return <ExperimentStatusPill experiment={{ ...r, stop_reason: null }} />;
  if (r.mean === null) return <span className="text-dim">—</span>;
  return (
    <span className="font-mono">
      {formatScore(r.mean)} <span className="text-muted">· pass {formatPercent(r.pass_rate)}</span>
    </span>
  );
}

const COLUMNS: Column<EvaluatorRun>[] = [
  { header: "Date", className: "text-muted", cell: (r) => formatDateTime(r.created_at) },
  { header: "Experiment", className: "font-semibold", cell: (r) => r.label },
  { header: "Dataset", className: "text-muted", cell: runDataset },
  {
    header: "Runner",
    className: "text-muted",
    cell: (r) => (r.runner === "agent" ? "Agent" : "Dataset outputs"),
  },
  { header: "Period", className: "text-muted", cell: (r) => formatPeriod(r.period) || "—" },
  { header: "Version", className: "font-mono", cell: (r) => `v${r.evaluator_version}` },
  { header: "Count", num: true, className: "font-mono", cell: (r) => r.n },
  { header: "Score", num: true, className: "text-right", cell: (r) => <Score run={r} /> },
];

export function RunsTable({ runs }: { runs: EvaluatorRun[] }) {
  const navigate = useNavigate();
  return (
    <Box>
      {runs.length === 0 ? (
        <EmptyState title="No experiments yet.">
          Start an experiment with this evaluator to see its scores here.
        </EmptyState>
      ) : (
        <DataTable
          columns={COLUMNS}
          rows={runs.map((r) => ({ ...r, id: r.experiment_id }))}
          onRowClick={(r) => void navigate(`/experiments/${r.experiment_id}`)}
          className="tbl text-xs"
        />
      )}
    </Box>
  );
}
